import { useCallback, useEffect, useMemo, useState } from "react";
import { AREA_CLUSTERS } from "./areaClusters";
import { clusterFields } from "./hooks";

const STORAGE_KEY = "jepx-lazer-desk-hidden-clusters";

const readHidden = (): string[] => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((x): x is string => typeof x === "string")
      : [];
  } catch {
    // A corrupt entry should not stop the desk from loading.
    return [];
  }
};

/**
 * Which order books are shown in the wide grid.
 *
 * Persisted because folding away half the books is a per-trader setup, not
 * something worth redoing on every reload. Reported as a DataGrid
 * `columnVisibilityModel` since that is how a cluster is folded.
 */
export const useVisibleClusters = () => {
  const [hidden, setHidden] = useState<string[]>(readHidden);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(hidden));
    } catch {
      // Private browsing / quota — the grid still works, just unpersisted.
    }
  }, [hidden]);

  const hiddenSet = useMemo(() => new Set(hidden), [hidden]);

  const hideCluster = useCallback((clusterId: string) => {
    setHidden((prev) =>
      prev.includes(clusterId) ? prev : [...prev, clusterId]
    );
  }, []);

  const toggleCluster = useCallback((clusterId: string) => {
    setHidden((prev) =>
      prev.includes(clusterId)
        ? prev.filter((id) => id !== clusterId)
        : [...prev, clusterId]
    );
  }, []);

  const showAll = useCallback(() => setHidden([]), []);

  const columnVisibilityModel = useMemo(() => {
    const model: Record<string, boolean> = {
      // Sort keys only — the delivery window is rendered in the Product column.
      contractDeliveryStartUtc: false,
      contractDeliveryEndUtc: false,
    };

    for (const cluster of AREA_CLUSTERS) {
      if (hiddenSet.has(cluster.id)) {
        for (const field of clusterFields(cluster.id)) {
          model[field] = false;
        }
      }
    }

    return model;
  }, [hiddenSet]);

  return {
    hiddenSet,
    hideCluster,
    toggleCluster,
    showAll,
    columnVisibilityModel,
  };
};
